/**
 * Empresas cliente — el panel de la gestoría (`OD-0063` D-06, `ADR-0025`).
 *
 * ```text
 * LAS FILAS     de GET /asesoria/panel, que lee la gestoria en GLOBAL_DB: que
 *               empresas, su plan y quien las lleva. Ningun dato de ellas
 * CADA RESUMEN  una peticion POR EMPRESA, con la empresa en la ruta, solo de
 *               las que estan en Business y lleva quien mira. De cuatro en
 *               cuatro, y guardado cinco minutos en la pestana
 * ENTRAR        el cambio de empresa de siempre (D-05), recordando a que
 *               gestoria volver
 * DESCARGAR     el Excel completo o el ZIP de UNA empresa, o el Excel de todas
 *               las de la lista en un ZIP armado aqui, de 100 en 100
 * ```
 *
 * Aquí no se decide nada: qué empresas, qué trimestres y quién puede leer qué
 * lo dice la API en cada petición. Una fila en gris no enseña ni una cifra.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  apiPanelAsesoria,
  type DescargaDelPanel,
  type FilaDelPanel,
  type PanelDeAsesoria,
  type ResumenDeCliente,
  type Sesion,
} from "../api/cliente";
import { t } from "../i18n/es";
import { ConversacionConLaAsesoria } from "./ConversacionConLaAsesoria";
import { diaLegible, importeLegible } from "../ui/formato";
import {
  almacenDeLaPestana,
  conDescargaHecha,
  estadoInicial,
  filtrarFilas,
  guardarResumen,
  nombreDePersona,
  ordenarFilas,
  personasDelPanel,
  quienDescargo,
  recordarVuelta,
  resumenEnCache,
  SIN_FILTROS,
  tandas,
  vaciarCache,
  volverAlPanel,
  vueltaRecordada,
  zipDeLaTanda,
  type DescargaDeLaTanda,
  type EstadoDeFila,
  type FiltrosDelPanel,
} from "../ui/panel-de-asesoria";
import { Aviso, Boton, Casilla, Selector } from "../ui/primitivas";
import { useCambioDeEmpresa } from "./CambioDeEmpresa";
import { SelectorDeEmpresa } from "./SelectorDeEmpresa";

/** Cuántos resúmenes se piden a la vez: 80 de golpe serían 80 peticiones abiertas. */
const A_LA_VEZ = 4;

/** Entrega el fichero al navegador como una descarga normal, con su nombre. */
function guardar(contenido: Blob, nombre: string): void {
  const url = URL.createObjectURL(contenido);
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Lo que se dice de una petición que no ha salido bien. */
function motivoDelFallo(estado: number): string {
  if (estado === 404) return t.panelAsesoria.yaNoDisponible;
  if (estado === 0) return t.comun.sinRed;
  return t.panelAsesoria.errorFila;
}

export function PantallaPanelAsesoria({
  sesion,
  panel,
  hayConexion,
  onCambioDeEmpresa,
  onSesionCaducada,
}: {
  sesion: Sesion;
  panel: PanelDeAsesoria;
  hayConexion: boolean;
  onCambioDeEmpresa: () => void;
  onSesionCaducada: () => void;
}) {
  const almacen = useMemo(almacenDeLaPestana, []);
  // La caché es de ESTA gestoría y ESTA persona: otra sesión no ve lo de la anterior.
  const dueno = `${sesion.organizationId}:${sesion.userId}`;
  const [trimestre, setTrimestre] = useState(panel.trimestres[0] ?? "");
  const [estados, setEstados] = useState<ReadonlyMap<string, EstadoDeFila>>(new Map());
  const [filtros, setFiltros] = useState<FiltrosDelPanel>(SIN_FILTROS);
  const [lecturas, setLecturas] = useState(0);
  const [descargando, setDescargando] = useState<string | null>(null);
  const [tanda, setTanda] = useState<{ readonly hechas: number; readonly total: number } | null>(null);
  const [mensaje, setMensaje] = useState<{ readonly tono: "error" | "exito" | "aviso"; readonly texto: string } | null>(null);
  const cambio = useCambioDeEmpresa(onCambioDeEmpresa, onSesionCaducada);
  const vigente = useRef(0);
  // En una ref: si fuera dependencia del efecto, cada render del padre con una
  // funcion nueva volveria a pedir los 80 resumenes.
  const caducada = useRef(onSesionCaducada);
  caducada.current = onSesionCaducada;

  const ponerEstado = (id: string, e: EstadoDeFila): void => setEstados((m) => new Map(m).set(id, e));

  // Los resúmenes: de la caché lo que tenga menos de cinco minutos, y lo demás
  // de la API, de A_LA_VEZ en A_LA_VEZ. Cambiar de trimestre o «Actualizar»
  // deja sin efecto lo que estuviera en camino.
  useEffect(() => {
    const turno = ++vigente.current;
    const inicial = new Map<string, EstadoDeFila>();
    const pendientes: FilaDelPanel[] = [];
    for (const f of panel.clientes) {
      const e = estadoInicial(f);
      const guardado = e.tipo === "cargando" ? resumenEnCache(almacen, dueno, f.organizationId, trimestre, Date.now()) : null;
      inicial.set(f.organizationId, guardado === null ? e : { tipo: "datos", resumen: guardado });
      if (e.tipo === "cargando" && guardado === null) pendientes.push(f);
    }
    setEstados(inicial);
    if (trimestre === "") return;
    const cola = [...pendientes];
    const trabajador = async (): Promise<void> => {
      for (let f = cola.shift(); f !== undefined; f = cola.shift()) {
        const r = await apiPanelAsesoria.resumen(f.organizationId, trimestre);
        if (vigente.current !== turno) return;
        if (r.ok) {
          guardarResumen(almacen, dueno, trimestre, r.datos, Date.now());
          ponerEstado(f.organizationId, { tipo: "datos", resumen: r.datos });
        } else if (r.estado === 401) {
          caducada.current();
          return;
        } else {
          ponerEstado(f.organizationId, { tipo: "error", estado: r.estado });
        }
      }
    };
    void Promise.all(Array.from({ length: Math.min(A_LA_VEZ, cola.length) }, trabajador));
  }, [panel, trimestre, lecturas, almacen, dueno]);

  const personas = useMemo(() => personasDelPanel(panel.clientes), [panel.clientes]);
  const visibles = ordenarFilas(filtrarFilas(panel.clientes, estados, filtros), estados);
  // «Descargar todas» baja las de la lista que se pueden descargar: con datos.
  const descargables = visibles.filter((f) => estados.get(f.organizationId)?.tipo === "datos");
  const ocupado = !hayConexion || descargando !== null || tanda !== null || cambio.cambiando;

  async function descargarUna(f: FilaDelPanel, que: DescargaDelPanel): Promise<void> {
    setMensaje(null);
    setDescargando(`${f.organizationId}:${que}`);
    const r = await apiPanelAsesoria.descargar(f.organizationId, que, trimestre);
    setDescargando(null);
    if (!r.ok) {
      if (r.estado === 401) onSesionCaducada();
      else setMensaje({ tono: "error", texto: r.error?.message ?? motivoDelFallo(r.estado) });
      return;
    }
    guardar(r.datos.contenido, r.datos.nombre);
    apuntarDescarga(f, que);
  }

  /** La descarga ya está en la auditoría de la empresa: se pinta sin volver a leer su shard. */
  function apuntarDescarga(f: FilaDelPanel, que: DescargaDelPanel): void {
    const e = estados.get(f.organizationId);
    if (e?.tipo !== "datos") return;
    const nuevo = conDescargaHecha(e.resumen, sesion.userId, que === "completo.xlsx" ? "completo.xlsx" : "zip", new Date());
    guardarResumen(almacen, dueno, trimestre, nuevo, Date.now(), true);
    ponerEstado(f.organizationId, { tipo: "datos", resumen: nuevo });
  }

  async function descargarTanda(lista: readonly FilaDelPanel[]): Promise<void> {
    setMensaje(null);
    setTanda({ hechas: 0, total: lista.length });
    const hechas: DescargaDeLaTanda[] = [];
    // Una detrás de otra: cada una es la descarga de siempre, con su auditoría
    // en SU shard, y 100 a la vez serían 100 Excel armándose en el Worker.
    for (const f of lista) {
      const e = estados.get(f.organizationId);
      const empresa = e?.tipo === "datos" ? e.resumen.empresa : null;
      const nombre = empresa?.nombre ?? f.nombre;
      const nif = empresa?.nif ?? null;
      const r = await apiPanelAsesoria.descargar(f.organizationId, "completo.xlsx", trimestre);
      if (!r.ok && r.estado === 401) {
        setTanda(null);
        onSesionCaducada();
        return;
      }
      if (r.ok) {
        hechas.push({ ok: true, nombre, nif, bytes: new Uint8Array(await r.datos.contenido.arrayBuffer()) });
        apuntarDescarga(f, "completo.xlsx");
      } else {
        hechas.push({ ok: false, nombre, nif, motivo: r.error?.message ?? motivoDelFallo(r.estado) });
      }
      setTanda({ hechas: hechas.length, total: lista.length });
    }
    const zip = zipDeLaTanda(hechas, trimestre, new Date().toISOString().slice(0, 10), {
      leeme: (fallos) => t.panelAsesoria.leeme(t.exportacion.nombreTrimestre(trimestre), fallos),
      sinNombre: t.panelAsesoria.sinNombre,
    });
    guardar(new Blob([zip.slice().buffer], { type: "application/zip" }), t.panelAsesoria.nombreDelZip(trimestre));
    const fallos = hechas.filter((h) => !h.ok).length;
    setTanda(null);
    setMensaje(
      fallos === 0
        ? { tono: "exito", texto: t.panelAsesoria.tandaHecha(hechas.length) }
        : { tono: "aviso", texto: t.panelAsesoria.tandaConFallos(hechas.length - fallos, fallos) },
    );
  }

  function entrar(f: FilaDelPanel): void {
    void cambio.cambiar(f.organizationId, () => recordarVuelta(almacen, sesion.organizationId));
  }

  const grupos = tandas(descargables);

  return (
    <section aria-labelledby="panel-asesoria-titulo">
      <h2 className="titulo-seccion" id="panel-asesoria-titulo">
        {t.panelAsesoria.titulo}
      </h2>
      <p className="ayuda-neutra">{t.panelAsesoria.explicacion}</p>

      <div className="filtros-panel-asesoria">
        <Selector
          id="panel-asesoria-trimestre"
          etiqueta={t.panelAsesoria.trimestre}
          valor={trimestre}
          opciones={panel.trimestres.map((x) => ({ valor: x, texto: t.exportacion.nombreTrimestre(x) }))}
          onChange={setTrimestre}
          deshabilitado={ocupado}
        />
        <Casilla
          id="panel-asesoria-rechazos"
          etiqueta={t.panelAsesoria.conRechazos}
          marcada={filtros.conRechazos}
          onChange={(v) => setFiltros({ ...filtros, conRechazos: v })}
        />
        <Casilla
          id="panel-asesoria-sin-descargar"
          etiqueta={t.panelAsesoria.sinDescargar}
          marcada={filtros.sinDescargar}
          onChange={(v) => setFiltros({ ...filtros, sinDescargar: v })}
        />
        {personas.length > 1 ? (
          <Selector
            id="panel-asesoria-persona"
            etiqueta={t.panelAsesoria.persona}
            valor={filtros.persona ?? ""}
            opciones={[{ valor: "", texto: t.panelAsesoria.todasLasPersonas }, ...personas.map((p) => ({ valor: p.userId, texto: nombreDePersona(p) }))]}
            onChange={(v) => setFiltros({ ...filtros, persona: v === "" ? null : v })}
          />
        ) : null}
      </div>

      <div className="acciones-en-linea">
        <Boton
          variante="terciario"
          deshabilitado={ocupado}
          onClick={() => {
            vaciarCache(almacen);
            setLecturas((n) => n + 1);
          }}
        >
          {t.panelAsesoria.actualizar}
        </Boton>
        {grupos.map((g, i) => (
          <Boton key={i} variante="secundario" deshabilitado={ocupado} onClick={() => void descargarTanda(g)}>
            {grupos.length === 1
              ? t.panelAsesoria.descargarTodas(g.length)
              : t.panelAsesoria.descargarTanda(i * 100 + 1, i * 100 + g.length, descargables.length)}
          </Boton>
        ))}
      </div>
      <p className="ayuda-neutra">{t.panelAsesoria.notaCache}</p>

      {tanda !== null ? <Aviso tono="info">{t.panelAsesoria.descargandoTanda(tanda.hechas, tanda.total)}</Aviso> : null}
      {mensaje !== null ? <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso> : null}
      {cambio.error !== null ? <Aviso tono="error">{cambio.error}</Aviso> : null}
      {cambio.confirmacion}

      {panel.clientes.length === 0 ? (
        <p className="ayuda-neutra">{t.panelAsesoria.sinClientes}</p>
      ) : visibles.length === 0 ? (
        <p className="ayuda-neutra">{t.panelAsesoria.sinCoincidencias}</p>
      ) : (
        <ul className="lista-asesoria">
          {visibles.map((f) => (
            <FilaDeCliente
              key={f.organizationId}
              fila={f}
              estado={estados.get(f.organizationId) ?? estadoInicial(f)}
              yo={sesion.userId}
              ocupado={ocupado}
              descargando={descargando}
              hayConexion={hayConexion}
              onEntrar={() => entrar(f)}
              onDescargar={(que) => void descargarUna(f, que)}
              onSesionCaducada={onSesionCaducada}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function FilaDeCliente({
  fila,
  estado,
  yo,
  ocupado,
  descargando,
  hayConexion,
  onEntrar,
  onDescargar,
  onSesionCaducada,
}: {
  fila: FilaDelPanel;
  estado: EstadoDeFila;
  yo: string;
  ocupado: boolean;
  descargando: string | null;
  hayConexion: boolean;
  onEntrar: () => void;
  onDescargar: (que: DescargaDelPanel) => void;
  onSesionCaducada: () => void;
}) {
  const r: ResumenDeCliente | null = estado.tipo === "datos" ? estado.resumen : null;
  // OD-0067 D-05: la conversacion con ESTA empresa, en la propia fila.
  const [hablando, setHablando] = useState(false);
  const [leidos, setLeidos] = useState(false);
  const nombre = r?.empresa?.nombre ?? fila.nombre ?? t.panelAsesoria.sinNombre;
  const gris = estado.tipo === "sin-acceso";
  return (
    <li className={gris ? "fila-asesoria fila-asesoria-gris" : "fila-asesoria"}>
      <div className="fila-asesoria-cabecera">
        <strong>{nombre}</strong>
        {r?.empresa !== null && r?.empresa !== undefined ? <span className="ayuda-neutra">{t.panelAsesoria.nif(r.empresa.nif)}</span> : null}
        {nombreDelPlan(fila.plan) !== null ? <span className="insignia insignia-CANCELLED">{nombreDelPlan(fila.plan)}</span> : null}
        {r !== null && r.aeat.rechazados > 0 ? (
          <span className="insignia insignia-REJECTED">{t.panelAsesoria.rechazados(r.aeat.rechazados)}</span>
        ) : null}
      </div>

      {gris ? (
        // D-06 SIN ACCESO: el motivo, y ni una cifra suya.
        <p className="ayuda-neutra">{t.asesoria.sinBusiness}</p>
      ) : (
        <p className="ayuda-neutra">
          {t.asesoria.llevan}: {fila.personas.length === 0 ? t.asesoria.nadie : fila.personas.map(nombreDePersona).join(", ")}
        </p>
      )}

      {estado.tipo === "no-la-llevas" ? <p className="ayuda-neutra">{t.panelAsesoria.noLaLlevas}</p> : null}
      {estado.tipo === "cargando" ? <p className="ayuda-neutra">{t.panelAsesoria.leyendo}</p> : null}
      {estado.tipo === "error" ? <p className="ayuda-neutra">{motivoDelFallo(estado.estado)}</p> : null}

      {r !== null ? (
        <>
          <ul className="cifras-asesoria">
            <li>
              {r.emitidas.calculado
                ? t.panelAsesoria.emitidas(r.emitidas.facturas, importeLegible(r.emitidas.base), importeLegible(r.emitidas.repercutido))
                : r.emitidas.motivo}
            </li>
            <li>
              {r.soportado.disponible
                ? t.panelAsesoria.soportado(r.soportado.compras, importeLegible(r.soportado.soportado))
                : r.soportado.motivo}
            </li>
            <li>
              {r.aeat.rechazados === 0 && r.aeat.pendientes === 0
                ? t.panelAsesoria.aeatAlDia
                : t.panelAsesoria.aeat(r.aeat.rechazados, r.aeat.pendientes)}
            </li>
            <li>
              {r.ultimaDescarga === null
                ? t.panelAsesoria.sinDescargarAun
                : t.panelAsesoria.descargadoPor(textoDeQuien(quienDescargo(r.ultimaDescarga, yo, fila.personas)), diaLegible(r.ultimaDescarga.en.slice(0, 10)))}
            </li>
            {/* OD-0067 D-03: lo que el cliente le dice de ESTE trimestre. */}
            {r.comentario !== null ? (
              <li>
                <strong>{t.panelAsesoria.comentario}:</strong> <span className="texto-con-saltos">{r.comentario}</span>
              </li>
            ) : null}
          </ul>
          <div className="acciones-en-linea">
            <Boton deshabilitado={ocupado} onClick={onEntrar}>
              {t.panelAsesoria.entrar}
            </Boton>
            <Boton variante="secundario" deshabilitado={ocupado} onClick={() => onDescargar("completo.xlsx")}>
              {descargando === `${fila.organizationId}:completo.xlsx` ? t.panelAsesoria.descargando : t.panelAsesoria.descargarExcel}
            </Boton>
            <Boton variante="terciario" deshabilitado={ocupado} onClick={() => onDescargar("facturas.zip")}>
              {descargando === `${fila.organizationId}:facturas.zip` ? t.panelAsesoria.descargando : t.panelAsesoria.descargarZip}
            </Boton>
            {r.noLeidos !== null ? (
              <Boton variante="terciario" onClick={() => setHablando((h) => !h)}>
                {hablando ? t.conversacionAsesoria.cerrar : t.conversacionAsesoria.enElPanel(leidos ? 0 : r.noLeidos)}
              </Boton>
            ) : null}
          </div>
          {hablando ? (
            <ConversacionConLaAsesoria
              organizationId={fila.organizationId}
              empresa={nombre}
              hayConexion={hayConexion}
              onSesionCaducada={onSesionCaducada}
              onLeida={() => setLeidos(true)}
            />
          ) : null}
        </>
      ) : null}
    </li>
  );
}

/** "BUSINESS" -> "Plan Business". Un plan que la web no conoce no se nombra. */
function nombreDelPlan(plan: string | null): string | null {
  return plan !== null && plan in t.panel.planes ? t.panel.planes[plan as keyof typeof t.panel.planes] : null;
}

function textoDeQuien(q: ReturnType<typeof quienDescargo>): string {
  if (q.tipo === "tu") return t.panelAsesoria.ti;
  if (q.tipo === "persona") return q.email;
  return t.panelAsesoria.laEmpresa;
}

/**
 * D-05 EL AVISO: dentro de la empresa de un cliente, una franja fija.
 *
 * «Volver al panel» vuelve a la gestoría de la que se entró, recordada en esta
 * pestaña; si no se sabe (se llegó por el selector, o en otra pestaña), se
 * enseña el selector para elegirla. Volver es el mismo cambio de empresa, con
 * sus dos fases en la API.
 */
export function FranjaDeAsesoria({
  empresa,
  onCambioDeEmpresa,
  onSesionCaducada,
}: {
  empresa: string | null;
  onCambioDeEmpresa: () => void;
  onSesionCaducada: () => void;
}) {
  const almacen = useMemo(almacenDeLaPestana, []);
  const [vuelta] = useState(() => vueltaRecordada(almacen));
  const [eligiendo, setEligiendo] = useState(false);
  const cambio = useCambioDeEmpresa(onCambioDeEmpresa, onSesionCaducada);
  return (
    <section className="aviso aviso-info franja-asesoria sin-imprimir" aria-label={t.panelAsesoria.franjaRegion}>
      <p>
        <strong>{t.panelAsesoria.franja(empresa ?? t.panelAsesoria.sinNombre)}</strong>
      </p>
      {cambio.error !== null ? <Aviso tono="error">{cambio.error}</Aviso> : null}
      {cambio.confirmacion}
      {eligiendo ? (
        <SelectorDeEmpresa id="franja" onCambiada={onCambioDeEmpresa} onSesionCaducada={onSesionCaducada} />
      ) : cambio.confirmacion === null ? (
        <Boton
          variante="secundario"
          deshabilitado={cambio.cambiando}
          onClick={() => (vuelta === null ? setEligiendo(true) : void cambio.cambiar(vuelta, () => volverAlPanel(almacen)))}
        >
          {cambio.cambiando ? t.panelAsesoria.volviendo : t.panelAsesoria.volver}
        </Boton>
      ) : null}
    </section>
  );
}
